import { isEqual } from "lodash-es";

import * as Cmp from "../comparison";

import { DataExtractorGroup } from "./extractor";
import { DataSource, ArrayDataSource, toArray } from "./source";
import { DataView, Sorter, getExtractor } from "./view";

class SortedDataView<ITEM> implements DataView<ITEM> {
    readonly dataSource: DataSource<ITEM>;
    readonly sorters: Array<Sorter>;
    readonly extractorGroups: Array<DataExtractorGroup<ITEM>>;
    readonly defaultCompareItems: Cmp.ComparisonFunction<ITEM>;

    constructor(baseDataView: DataView<ITEM>, sorters: Array<Sorter>) {
        this.sorters = sorters;
        const comparisonFunctions: Array<(item1: ITEM, item2: ITEM) => number> =
            sorters.map((sorter) => {
                const { extractorId, direction } = sorter;
                const extractor = getExtractor(baseDataView, extractorId);
                if (extractor === undefined) {
                    throw new Error("Unknown extractor id: " + extractorId);
                }
                let comparisonFunction: (item1: ITEM, item2: ITEM) => number;
                switch (direction) {
                    case "ASC":
                        comparisonFunction = (item1, item2) =>
                            extractor.compare(item1, item2);
                        break;
                    case "DESC":
                        comparisonFunction = (item1, item2) =>
                            extractor.compare(item2, item1);
                        break;
                    default:
                        throw new Error("Unknown direction: " + direction);
                }
                return comparisonFunction;
            });
        const direction =
            sorters.length > 0 ? sorters[sorters.length - 1].direction : "ASC";
        if (baseDataView.defaultCompareItems) {
            switch (direction) {
                case "ASC":
                    comparisonFunctions.push(baseDataView.defaultCompareItems);
                    break;
                case "DESC":
                    comparisonFunctions.push(
                        Cmp.inverse(baseDataView.defaultCompareItems)
                    );
                    break;
                default:
                    throw new Error("Unknown direction: " + direction);
            }
        }
        // Fallback on comparison by id
        const idComparisonFunction = (item1: ITEM, item2: ITEM) =>
            baseDataView.dataSource
                .getId(item1)
                .localeCompare(baseDataView.dataSource.getId(item2));
        switch (direction) {
            case "ASC":
                comparisonFunctions.push(idComparisonFunction);
                break;
            case "DESC":
                comparisonFunctions.push(Cmp.inverse(idComparisonFunction));
                break;
            default:
                throw new Error("Unknown direction: " + direction);
        }
        const items = toArray(baseDataView.dataSource.iter());
        if (comparisonFunctions.length > 0) {
            items.sort(Cmp.chain(comparisonFunctions));
        }
        this.dataSource = new ArrayDataSource(items, (item: ITEM) =>
            baseDataView.dataSource.getId(item)
        );
        this.extractorGroups = baseDataView.extractorGroups;
        this.defaultCompareItems = baseDataView.defaultCompareItems;
    }
}

function sorted<ITEM>(dataView: DataView<ITEM>, sorters: Array<Sorter>) {
    return isEqual(dataView.sorters, sorters)
        ? dataView
        : new SortedDataView(dataView, sorters);
}

export { SortedDataView, sorted };
